// Read historical order data only. Never consult today's catalog or promotions.
export const money = value => {
    if ((typeof value !== 'number' && typeof value !== 'string') || String(value).trim() === '') return null
    const number = Number(value)
    return Number.isFinite(number) ? Math.round((number + Number.EPSILON) * 100) : null
}

const carrierMoney = value => ['string', 'number'].includes(typeof value)
    && /^\d+(\.\d{1,2})?$/.test(String(value)) && Number(value) <= 99999999.99 ? money(value) : null

const sum = values => values.reduce((total, value) => total + value, 0)
const completeSum = values => values.length && values.every(value => value !== null) ? sum(values) : null
const snapshots = value => Array.isArray(value) ? value.filter(discount => discount && typeof discount === 'object') : []

export const discountLabel = (discount, couponCode) => {
    const code = discount.code || (discount.type === 'code' ? couponCode : null)
    const name = discount.name || 'Descuento sin nombre registrado'
    const percentage = discount.discountType === 'percentage' && money(discount.value) !== null
        ? ` (${Number(discount.value)}%)` : ''
    return `${name}${code ? ` · Código: ${code}` : ''}${percentage}${discount.freeShipping ? ' · Envío gratis' : ''}`
}

// SkyDropX documents attributes.total as shipment cost and payment_status=paid
// in the purchase response: https://pro.skydropx.com/es-MX/api-docs
// Read retained purchase/reconciliation evidence only, never a quotation.
const evidenceCost = (response, order) => {
    const data = response?.data ?? response
    const shipments = Array.isArray(data) ? data : data ? [data] : []
    if (!shipments.length) return null
    const expectedIds = Array.isArray(order.shipmentMetadata?.skydropxShipmentIds) ? order.shipmentMetadata.skydropxShipmentIds : []
    const ids = shipments.map(shipment => `${shipment?.id || shipment?.attributes?.id || shipment?.attributes?.shipment_id || ''}`)
    if (ids.some(id => !id) || new Set(ids).size !== ids.length
        || expectedIds.some(id => !ids.includes(`${id}`))) return null
    const costs = shipments.map(shipment => {
        const attributes = shipment.attributes || shipment
        const currency = attributes.currency_code || attributes.currency
        const cost = carrierMoney(attributes.total)
        // The integration handles domestic MXN shipments. Refuse another or
        // unknown currency rather than subtracting incomparable amounts.
        return attributes.payment_status === 'paid' && currency === 'MXN' && cost !== null && cost >= 0 ? cost : null
    })
    return completeSum(costs)
}

export const purchasedCarrierCost = (order = {}) => evidenceCost(order.shipmentMetadata?.skydropxShipment, order)
    ?? evidenceCost(order.shipmentMetadata?.skydropxReconciledShipment, order)

export const actualCarrierCost = (order = {}) => {
    const manual = order.shipmentMetadata?.actualShippingCost
    const cost = manual?.source === 'manual' && manual?.currency === 'MXN' ? carrierMoney(manual.amount) : null
    return purchasedCarrierCost(order) ?? (cost !== null && cost >= 0 ? cost : null)
}

export const orderFinancials = (order = {}) => {
    const items = Array.isArray(order.lineItems) ? order.lineItems : []
    const orderDiscounts = snapshots(order.appliedDiscounts)
    // Order snapshots summarize item allocations. Never add the two together.
    const details = orderDiscounts.length ? orderDiscounts : items.flatMap(item => snapshots(item.appliedDiscounts))
    const subtotal = money(order.subtotal) ?? completeSum(items.map(item => money(item.subtotal)))
    const shipping = money(order.shippingCosts)
    const total = money(order.total)
    const tax = money(order.taxAmount)
    const itemDiscount = completeSum(items.map(item => money(item.discount)))
    const discount = money(order.discount)
        ?? completeSum([money(order.automaticDiscount), money(order.codeDiscount)])
        ?? itemDiscount
        ?? completeSum(details.map(detail => money(detail.amount)))
    const detailTotal = completeSum(details.map(detail => money(detail.amount)))
    const unexplained = subtotal !== null && discount !== null && shipping !== null && total !== null
        ? total - (subtotal - discount + shipping + (tax ?? 0)) : null
    const carrierCost = actualCarrierCost(order)
    const carrierCostStatus = 'Pendiente'
    return {
        subtotal, shipping, total, tax, discount, details, unexplained, carrierCostStatus, carrierCost,
        shippingContribution: shipping !== null && carrierCost !== null ? shipping - carrierCost : null,
        detailMismatch: detailTotal !== null && discount !== null && detailTotal !== discount
    }
}
